"use client";

import React, {useState, useRef, useEffect, type RefCallback} from "react";
import {useClickAway} from "react-use";

import {InputValidator} from "../";
import {ScrollableList, type ScrollableListRef} from "./ScrollableList";
//import {Label} from "../";

export type ListData = {
  label: string,
  value: string,
  code: string,
  imageUrl?: string
}

type InputProps =
  React.InputHTMLAttributes<HTMLInputElement>
  & {
  parentRef: RefCallback<HTMLInputElement>;
  inputRef?: any;
  mask?: RegExp;
  format?: "properCase" | "email" | "phone" | "currency" | "number";
  country: ListData;
  countries: ListData[];
  listClass?: string;
  initialValue?: string;
  error?: string;
  showToast?: (message: string) => void;
  onFocus?: () => void;
  onChange?: (value: any) => void;
};

const PhoneInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({
     parentRef,
     inputRef,
     className,
     type = "phone",
     minLength,
     maxLength,
     mask,
     format,
     country,
     listClass = "bottom-full mb-3",
     initialValue,
     error,
     onFocus,
     onChange,
     onBlur,
     countries,
     showToast,
     ...props
   }
  ) => {
    const defaultCountry = country;
    const [selectedCountry, setSelectedCountry] = useState<ListData>(defaultCountry);
    const [selectedCode, setSelectedCode] = useState(defaultCountry.code);
    const [filteredCountries, setFilteredCountries] = useState<ListData[]>(countries);
    const [isOpen, setIsOpen] = useState(false);
    const [phoneValue, setPhoneValue] = useState(initialValue || "");
    //const initialVal = initialValue ?? (inputRef.current?.value ?? "");
    //const [hasValue, setHasValue] = useState(Boolean(String(initialVal).trim()));
    const [searchValue, setSearchValue] = useState("");
    const scrollableListRef = useRef<ScrollableListRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const isSelectingCountry = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // useEffect(() => {
    //   const domVal = inputRef.current?.value;
    //   const v = props.value ?? props.defaultValue ?? domVal ?? "";
    //   setHasValue(Boolean(String(v).trim()));
    // }, [props.value, props.defaultValue]);

    useEffect(() => {
      if (isOpen && scrollableListRef.current) {
        setTimeout(() => {
          scrollableListRef.current?.scrollToElement(`[data-country-code="${selectedCountry?.code}"]`)
          searchRef.current?.focus();
        }, 50)
      }
    }, [isOpen, selectedCountry]);

    useEffect(() => {
      setFilteredCountries(countries.filter((country) =>
        country.label.toLowerCase().includes(searchValue.toLowerCase())
      ));
    }, [countries, searchValue]);

    useEffect(() => {
      setIsOpen(false);
      setPhoneValue(initialValue || "");
      setSelectedCountry(defaultCountry);
      setSelectedCode(defaultCountry.code);
      setFilteredCountries(countries);
      setSearchValue("");
    }, []);

    useClickAway(containerRef, () => {
      if (isOpen) {
        setIsOpen(false);
      }
    });

    const handleCountrySelect = (country: ListData) => {
      setSelectedCountry(country);
      setSelectedCode(country.code);
      setIsOpen(false);
      setSearchValue("");
      triggerChange(phoneValue.replace(/\+\d+\s/g, `+${country.value} `), country);

      const t = setTimeout(() => {
        if (inputRef?.current) {
          inputRef.current.focus();
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
        clearTimeout(t);
      }, 0);
    }

    const handleButtonClick = () => {
      setIsOpen(!isOpen)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      triggerChange(e.target.value);
    }

    const triggerChange = (value: string, country: ListData = selectedCountry) => {
      setPhoneValue(value);
      //setHasValue(Boolean(String(value).trim()));
      const phone = {
        country: {
          code: country.code,
          name: country.label,
          dialCode: country.value
        },
        number: value.replace(/^\+\d+\s*/, ``).replace(/\s/g, ``)
      }
      onChange && onChange(phone);
    }

    const handleInputFocus = (_e: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.();
      setIsOpen(false);
    }

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!isSelectingCountry.current) {
        setIsOpen(false);
      }
      onBlur && onBlur(e);
    }

    return (
      <div
        ref={containerRef}
        className="space-y-4 w-full">
        <div
          className="relative">
          <InputValidator
            ref={inputRef}
            placeholder={props.placeholder}
            minLength={minLength}
            maxLength={maxLength}
            mask={mask}
            format={format}
            countryCode={selectedCountry?.value}
            buttonIcon={`flag-icon flag-icon-${selectedCountry?.code}`}
            showToast={showToast}
            value={phoneValue}
            error={error}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onButtonClick={handleButtonClick}
            {...props}
          />
          {/*{hasValue && (*/}
          {/*  <Label*/}
          {/*    className="absolute top-[-8px] left-[13px] text-[10px] font-semibold text-generic-dull">{props.placeholder}</Label>*/}
          {/*)}*/}

          {isOpen && (
            <div
              className={`countries-list absolute left-0 ${listClass} z-50 border-none p-0 w-full`}>
              <ScrollableList
                ref={scrollableListRef}
                maxHeight={150}
                onDragStateChange={setIsDragging}
                key={`${searchValue}-${filteredCountries.length}`}>
                {filteredCountries.length > 0 ? (
                  <div
                    className="space-y-1">
                    {filteredCountries.map((country) => (
                      <div
                        key={country.code}
                        data-country-code={country.code}
                        className={`flex items-center gap-2 px-2 py-2 cursor-grab hover:bg-bluegray-light/12 text-left ${
                          selectedCode === country.code ? "bg-bluegray-light/30" : ""
                        }`}
                        onClick={() => {
                          if (!isDragging) {
                            handleCountrySelect(country);
                          }
                        }}
                        onTouchEnd={() => {
                          if (!isDragging) {
                            handleCountrySelect(country);
                          }
                        }}
                        onMouseDown={() => {
                          isSelectingCountry.current = true;
                        }}
                        onMouseUp={() => {
                          isSelectingCountry.current = false;
                        }}
                      >
                        <img
                          src={country.imageUrl || "/assets/fonts/flag-icons/flags/png/noflag.png"}
                          alt={""}
                          className="w-5 h-4 object-cover shrink-0"
                          loading="lazy"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement
                            if (target.src !== "/assets/fonts/flag-icons/flags/png/noflag.png") {
                              target.src = "/assets/fonts/flag-icons/flags/png/noflag.png"
                            }
                          }}
                        />
                        <span
                          className="text-sm text-pale-white">{country.label}</span>
                        <span
                          className="text-xs text-muted-foreground ml-auto">+{country.value}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className="p-4 text-center text-sm text-muted-foreground">
                    No
                    countries
                    found!
                  </div>
                )}
              </ScrollableList>

              <div
                className="relative mb-1">
                <div
                  className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i
                    className="fa-duotone fa-light fa-magnifying-glass text-sm text-generic-dull"/>
                </div>

                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search countries..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onBlur={handleInputBlur}
                  className="w-full pl-8 pr-3 py-2 text-sm text-pale-white border-none focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
);

PhoneInput.displayName = "PhoneInput"
export {PhoneInput}
